/**
 * Derives a step's calculated fields from its current inputs (CALC-010, CALC-012, CALC-014).
 *
 * Pure and stateless on purpose: there is no cache to go stale (CALC-012) — every call recomputes from the
 * inputs it is given, so a corrected input always yields a fresh result. Arithmetic is decimal.js (CALC-005).
 */
import type { MbrContent } from "../mbr/content.js";
import { evaluateFormula, type FormulaResult } from "./formula.js";

type Step = MbrContent["procedure"]["steps"][number];

export interface DerivedValue {
  code: string;
  result: FormulaResult;
  /** Input codes and values the result was computed from, kept for the supersession reason (CALC-010). */
  inputs: Record<string, string>;
}

/** Formulas of `step` whose every input has a current value; others are not yet computable and are skipped. */
export function deriveFormulaValues(step: Step, current: Readonly<Record<string, string>>): DerivedValue[] {
  const out: DerivedValue[] = [];
  for (const f of step.formulas) {
    if (!f.inputs.every((i) => Object.hasOwn(current, i))) continue;
    const inputs = Object.fromEntries(f.inputs.map((i) => [i, current[i] as string]));
    out.push({
      code: f.code,
      inputs,
      result: evaluateFormula(f.expression, inputs, { precision: f.precision, rounding: f.rounding }),
    });
  }
  return out;
}

export const isFormulaCode = (step: Step, code: string): boolean =>
  step.formulas.some((f) => f.code === code);
